// data/items/setBonusData.js
// Set bonus definitions for Ashforged (melee), Cindermark (ranged), Pyreweave (magic)
// Only AWAKENED set pieces count toward thresholds
// Bonuses stack: 4pc includes 2pc, 6pc includes 2pc + 4pc

export const SET_BONUSES = {
    ashforged: {
        name: 'Ashforged',
        style: 'melee',
        bonuses: {
            2: {
                name: 'Momentum',
                description: 'Consecutive hits on the same target build Momentum: +2% damage per stack (max 10 stacks, +20%). Resets on target change.',
                type: 'momentum',
                damagePerStack: 0.02,
                maxStacks: 10
            },
            4: {
                name: 'Devastating Blow',
                description: 'At max Momentum (10 stacks), attacks have a 20% chance to deal 4x damage and heal for 50% of damage dealt.',
                type: 'devastatingBlow',
                procChance: 0.20,
                damageMultiplier: 4,
                lifestealPercent: 0.50,
                requiresMaxMomentum: true
            },
            6: {
                name: 'Weapon Mastery',
                description: '2H Sword: Afterimage clone fights for 10s at 50% damage (30s CD). 1H Sword: Riposte counters every 3rd hit taken at 75% damage.',
                type: 'weaponMastery',
                afterimage: {
                    duration: 10000,
                    cooldown: 30000,
                    damagePercent: 0.50
                },
                riposte: {
                    hitInterval: 3,
                    damagePercent: 0.75
                }
            }
        }
    },

    cindermark: {
        name: 'Cindermark',
        style: 'ranged',
        bonuses: {
            2: {
                name: 'Barbed Acceleration',
                description: 'Bleeds tick twice as fast.',
                type: 'bleedAcceleration',
                bleedSpeedMultiplier: 2
            },
            4: {
                name: 'Piercing Shot',
                description: 'Every 5th hit fires a Piercing Shot dealing 3.5x damage that ignores 50% of defense.',
                type: 'piercingShot',
                hitInterval: 5,
                damageMultiplier: 3.5,  // Buffed from 3x to 3.5x
                defenseIgnorePercent: 0.50
            },
            6: {
                name: 'Phantom Archer',
                description: 'After every Piercing Shot, a spectral archer fires 3 rapid shots at 70% damage over 2s.',
                type: 'phantomArcher',
                shotCount: 3,
                shotDamagePercent: 0.70,  // Buffed from 60% to 70%
                shotDuration: 2000
            }
        }
    },

    pyreweave: {
        name: 'Pyreweave',
        style: 'magic',
        bonuses: {
            2: {
                name: 'Spell Echo',
                description: 'Spells have a 30% chance to recast immediately at 80% power.',
                type: 'spellEcho',
                echoChance: 0.30,        // Buffed from 25% to 30%
                echoPowerPercent: 0.80   // Buffed from 75% to 80%
            },
            4: {
                name: 'Arcane Cascade',
                description: 'Elemental Cascade fires a 3rd spell at 60% power. Echoed spells can trigger Cascade.',
                type: 'arcaneCascade',
                thirdSpellPower: 0.60,   // Buffed from 50% to 60%
                echoCanCascade: true
            },
            6: {
                name: 'Spell Fusion',
                description: 'When 2+ spell effects are simultaneously active, they fuse into combo effects.',
                type: 'spellFusion',
                fusions: {
                    shatter: {
                        name: 'Shatter',
                        elements: ['dot', 'slow'],  // fire + ice
                        effect: 'nextHit2x',
                        cooldown: 3000,
                        description: 'Target is brittle — next hit deals 2x damage. Resets after triggering, reapplies after 3s.'
                    },
                    magma: {
                        name: 'Magma',
                        elements: ['dot', 'defense'],  // fire + earth
                        effect: 'mobDefenseReduction',
                        value: 0.15,
                        description: 'Mob defense reduced by 15% while both effects active.'
                    },
                    firestorm: {
                        name: 'Firestorm',
                        elements: ['dot', 'crit'],  // fire + air
                        effect: 'dotEnhance',
                        tickSpeedBonus: 0.50,
                        damageBonus: 0.30,
                        description: 'Fire DoT ticks 50% faster and deals 30% more damage.'
                    },
                    permafrost: {
                        name: 'Permafrost',
                        elements: ['slow', 'defense'],  // ice + earth
                        effect: 'deepFreeze',
                        attackSpeedReduction: 0.25,
                        description: 'Mob attack speed reduced by 25% (stronger than normal slow).'
                    },
                    hailstorm: {
                        name: 'Hailstorm',
                        elements: ['slow', 'crit'],  // ice + air
                        effect: 'damageAmp',
                        value: 0.08,
                        description: 'Mob takes 8% more damage from all sources.'
                    },
                    harmony: {
                        name: 'Harmony',
                        elements: ['defense', 'crit'],  // earth + air
                        effect: 'buffEnhance',
                        buffStrengthBonus: 0.40,
                        autoRefreshInterval: 5000,
                        description: 'Defense and crit buffs are 40% stronger and auto-refresh every 5s.'
                    }
                }
            }
        }
    }
};

// Quick lookup: which set bonuses are active for a given piece count
export function getActiveBonuses(setId, pieceCount) {
    const setData = SET_BONUSES[setId];
    if (!setData) return [];

    const active = [];
    if (pieceCount >= 2) active.push({ threshold: 2, ...setData.bonuses[2] });
    if (pieceCount >= 4) active.push({ threshold: 4, ...setData.bonuses[4] });
    if (pieceCount >= 6) active.push({ threshold: 6, ...setData.bonuses[6] });
    return active;
}

// Get set bonus data by setId
export function getSetBonusData(setId) {
    return SET_BONUSES[setId] || null;
}

export default SET_BONUSES;
